import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Card } from '../components/Card';
import { Text } from '../components/Text';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { UserState } from '../user/UserState';
import { AlertPresenter } from '../AlertBinder';

interface ITaskNotificationTestResponse {
    scenario: string;
    taskCounts: {
        completedToday: number;
        dueToday: number;
        overdue: number;
        dueTomorrow: number;
        completed: number;
        incomplete: number;
    };
    message: {
        title: string;
        body: string;
    };
    userId: string;
}

export const TasksNotificationDevTools: FC = () => {
    const apiClient = useInstance(APIClient);
    const userState = useInstance(UserState);
    const alertPresenter = useInstance(AlertPresenter);
    const [testLoading, setTestLoading] = useState(false);
    const [scheduleLoading, setScheduleLoading] = useState(false);

    const testNotification = async () => {
        setTestLoading(true);
        try {
            // Use the test link from user's links (standard HATEOAS pattern)
            const testLink = userState.state?.links?.testTasksNotification;
            if (!testLink) {
                throw new Error('Test endpoint not available. Please reload the app.');
            }

            const response = await apiClient.get(testLink) as ITaskNotificationTestResponse;

            const statsText = `
Completed Today: ${response.taskCounts.completedToday}
Due Today: ${response.taskCounts.dueToday}
Overdue: ${response.taskCounts.overdue}
Due Tomorrow: ${response.taskCounts.dueTomorrow}
Completed (Total): ${response.taskCounts.completed}
Incomplete (Total): ${response.taskCounts.incomplete}
            `.trim();

            await alertPresenter.showAlertV2({
                header: `Scenario: ${response.scenario}`,
                subHeader: response.message.title,
                message: `${response.message.body}\n\n━━━━━━━━━━━━━━━━━━━━\n\nTask Statistics:\n${statsText}`,
                backdropDismiss: true,
                options: [{
                    id: 'ok',
                    label: 'OK'
                }]
            });
        } catch (error: any) {
            await alertPresenter.showAlertV2({
                header: 'Error',
                message: error.message || 'Failed to test notification',
                backdropDismiss: true,
                options: [{
                    id: 'ok',
                    label: 'OK'
                }]
            });
        } finally {
            setTestLoading(false);
        }
    };

    const scheduleNotifications = async () => {
        setScheduleLoading(true);
        try {
            const testLink = userState.state?.links?.testTasksNotification;
            if (!testLink) {
                throw new Error('Tasks notifications endpoint not available. Please reload the app.');
            }

            // Build the trigger endpoint from the test endpoint base
            const triggerLink = {
                href: testLink.href.replace('/test/tasks-notification', '/test/trigger-reminder-scan')
            };

            await apiClient.post(triggerLink, {}, {
                'content-type': 'application/json',
                accept: 'application/json'
            });

            await alertPresenter.showAlertV2({
                header: 'Success',
                message: 'Task reminder scan triggered successfully. Notifications have been scheduled for tasks with upcoming times.',
                backdropDismiss: true,
                options: [{
                    id: 'ok',
                    label: 'OK'
                }]
            });
        } catch (error: any) {
            await alertPresenter.showAlertV2({
                header: 'Error',
                message: error.message || 'Failed to trigger reminder scan',
                backdropDismiss: true,
                options: [{
                    id: 'ok',
                    label: 'OK'
                }]
            });
        } finally {
            setScheduleLoading(false);
        }
    };

    return (
        <div style={{marginBottom: '16px'}}>
            <Card>
                <div style={{padding: '16px'}}>
                    <div style={{marginBottom: '12px'}}>
                        <Text><b>Task Notification Dev Tools</b></Text>
                    </div>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                        <IonButton
                            fill="outline"
                            color="secondary"
                            size="small"
                            data-id="test-task-notification-button"
                            onClick={testNotification}
                            disabled={testLoading || scheduleLoading}
                        >
                            {testLoading ? 'Testing...' : 'Test My Notification'}
                        </IonButton>
                        <IonButton
                            fill="outline"
                            color="secondary"
                            size="small"
                            data-id="schedule-notifications-button"
                            onClick={scheduleNotifications}
                            disabled={testLoading || scheduleLoading}
                        >
                            {scheduleLoading ? 'Scheduling...' : 'Schedule Notifications'}
                        </IonButton>
                    </div>
                </div>
            </Card>
        </div>
    );
};
